import "leaflet/dist/leaflet.css";
import "@/App.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { http, getDeviceId, nowIstanbulHour, errMsg } from "@/lib/api";
import { PARAMS } from "@/lib/params";
import { MapView } from "@/components/weather/MapView";
import { TopBar } from "@/components/weather/TopBar";
import { SidePanel } from "@/components/weather/SidePanel";
import { TimeBar } from "@/components/weather/TimeBar";
import { PointDrawer } from "@/components/weather/PointDrawer";
import { ReportModal } from "@/components/weather/ReportModal";
import { SosModal } from "@/components/weather/SosModal";
import { RoutePanel } from "@/components/weather/RoutePanel";
import { NavHud, useNavigation } from "@/components/weather/NavHud";
import { I18nContext, useI18n } from "@/lib/i18n";

const useReports = () => {
  const { t } = useI18n();
  const deviceId = getDeviceId();
  const [reports, setReports] = useState([]);
  const load = useCallback(() => http.get("/reports", { params: { device_id: deviceId } }).then((r) => setReports(r.data)).catch(() => {}), [deviceId]);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);
  const add = (r) => setReports((x) => [r, ...x]);
  const confirm = async (id) => {
    try {
      const { data } = await http.post(`/reports/${id}/confirm`, { device_id: deviceId });
      setReports((x) => x.map((r) => (r.id === id ? data : r)));
      toast.success(t("Onayınız kaydedildi"));
    } catch (e) { toast.error(t(errMsg(e, "Onaylanamadı"))); }
  };
  return { reports, add, confirm };
};

const useFavorites = () => {
  const { t } = useI18n();
  const deviceId = getDeviceId();
  const [favorites, setFavorites] = useState([]);
  useEffect(() => { http.get("/favorites", { params: { device_id: deviceId } }).then((r) => setFavorites(r.data)).catch(() => {}); }, [deviceId]);
  const add = async (p) => {
    if (favorites.some((f) => Math.abs(f.lat - p.lat) < 0.01 && Math.abs(f.lon - p.lon) < 0.01)) return toast.info(t("Bu konum zaten favorilerde"));
    const { data } = await http.post("/favorites", { device_id: deviceId, name: p.name || `${p.lat.toFixed(2)}, ${p.lon.toFixed(2)}`, lat: p.lat, lon: p.lon });
    setFavorites((f) => [...f, data]);
    toast.success(t("{name} favorilere eklendi", { name: data.name }));
  };
  const remove = async (id) => {
    await http.delete(`/favorites/${id}`, { params: { device_id: deviceId } });
    setFavorites((f) => f.filter((x) => x.id !== id));
  };
  return { favorites, add, remove };
};

const usePersisted = (key, def) => {
  const [v, setV] = useState(() => localStorage.getItem(key) || def);
  useEffect(() => { localStorage.setItem(key, v); }, [key, v]);
  return [v, setV];
};

export default function App() {
  const [lang, setLang] = usePersisted("hd_lang", "tr");
  const [theme, setTheme] = usePersisted("hd_theme", "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    document.documentElement.lang = lang;
  }, [theme, lang]);
  return (
    <I18nContext.Provider value={{ lang }}>
      <Main lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} />
    </I18nContext.Provider>
  );
}

function Main({ lang, setLang, theme, setTheme }) {
  const { t: tr_ } = useI18n();
  const [grid, setGrid] = useState(null);
  const [paramId, setParamId] = useState("rain");
  const [t, setT] = useState(0);
  const [nowIdx, setNowIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [selected, setSelected] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [layers, setLayers] = useState({ values: true, reports: true });
  const [panelOpen, setPanelOpen] = useState(window.innerWidth > 768);
  const [reportOpen, setReportOpen] = useState(false);
  const [sosOpen, setSosOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [route, setRoute] = useState({ from: null, to: null, depart: 0, data: null });
  const [installPrompt, setInstallPrompt] = useState(null);
  const center = useRef({ lat: 39, lon: 35.2 });
  const { reports, add: addReport, confirm } = useReports();
  const { favorites, add: addFav, remove: removeFav } = useFavorites();
  const nav = useNavigation(route, setRoute, reports);
  const param = PARAMS.find((p) => p.id === paramId);

  useEffect(() => {
    http.get("/grid").then(({ data }) => {
      const i = Math.max(0, data.times.indexOf(nowIstanbulHour()));
      setGrid(data); setT(i); setNowIdx(i);
    }).catch(() => toast.error(tr_("Hava durumu verisi alınamadı")));
    const q = new URLSearchParams(window.location.search);
    if (q.get("lat") && q.get("lon")) {
      const p = { lat: +q.get("lat"), lon: +q.get("lon"), name: q.get("name") || undefined };
      setSelected(p); setFlyTarget(p);
      window.history.replaceState(null, "", "/");
    }
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!playing || !grid) return;
    const id = setInterval(() => setT((x) => (x + 1) % grid.times.length), 800);
    return () => clearInterval(id);
  }, [playing, grid]);

  const pick = (p) => { setSelected(p); setFlyTarget({ ...p }); };
  const reportLoc = selected ? { ...selected, src: "Seçili nokta" } : { ...center.current, src: "Harita merkezi" };
  const install = async () => { installPrompt.prompt(); await installPrompt.userChoice; setInstallPrompt(null); };
  const toggleRoute = () => { setRouteOpen((o) => !o); setPanelOpen(false); setSelected(null); };

  return (
    <div className="app-root" data-testid="app-root">
      <MapView grid={grid} param={param} t={t} layers={layers} reports={reports} selected={selected}
        onSelect={(s) => { setSelected({ lat: s.lat, lon: s.lon }); const i = s.time && grid ? grid.times.indexOf(s.time) : -1; if (i >= 0) setT(i); }} onMove={(c) => (center.current = c)} flyTarget={flyTarget} onConfirm={confirm}
        routeData={routeOpen || nav.active ? route.data : null} theme={theme} nav={nav} />
      <TopBar onPick={pick} onReport={() => setReportOpen(true)} onSos={() => setSosOpen(true)} updatedAt={grid?.updated_at}
        onRoute={toggleRoute} routeOpen={routeOpen} favorites={favorites} theme={theme} setTheme={setTheme} lang={lang} setLang={setLang} />
      {nav.active ? (
        <NavHud nav={nav} route={route} />
      ) : routeOpen ? (
        <RoutePanel reports={reports} onStartNav={() => { setSelected(null); nav.start(); }} route={route} setRoute={setRoute} onClose={() => setRouteOpen(false)} onSelect={(s) => { setSelected({ lat: s.lat, lon: s.lon }); const i = grid?.times.indexOf(s.time) ?? -1; if (i >= 0) setT(i); }} />
      ) : panelOpen ? (
        <SidePanel paramId={paramId} setParamId={setParamId} layers={layers} setLayers={setLayers} favorites={favorites}
          onPick={pick} onRemoveFav={removeFav} onClose={() => setPanelOpen(false)} onReport={() => setReportOpen(true)}
          installPrompt={installPrompt} onInstall={install} theme={theme} setTheme={setTheme} />
      ) : (
        <button data-testid="side-panel-open" onClick={() => setPanelOpen(true)} className="glass absolute left-3 top-20 z-[1000] flex items-center gap-2 rounded-full px-4 py-2 text-sm text-slate-200">
          <Layers size={16} className="text-sky-400" /> {tr_(param.label)}
        </button>
      )}
      {selected && !nav.active && <PointDrawer selected={selected} time={grid?.times[t]} reports={reports} onClose={() => setSelected(null)} onFavorite={() => addFav(selected)} />}
      {!nav.active && <TimeBar times={grid?.times || []} t={t} setT={setT} playing={playing} setPlaying={setPlaying} param={param} nowIdx={nowIdx} />}
      {!grid && (
        <div data-testid="grid-loading" className="glass absolute left-1/2 top-1/2 z-[1200] flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full px-5 py-3 text-sm text-slate-200">
          <Loader2 size={16} className="animate-spin text-sky-400" /> {tr_("Tahmin verileri yükleniyor…")}
        </div>
      )}
      <ReportModal open={reportOpen} onOpenChange={setReportOpen} defaultLoc={reportLoc} onCreated={addReport} />
      <SosModal open={sosOpen} onOpenChange={setSosOpen} onCreated={addReport} />
      <Toaster position="top-center" theme={theme} />
    </div>
  );
}
